import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../services/api";

import ManagerBilling from "../components/manager/ManagerBilling";
import InventoryManager from "../components/manager/inventory/InventoryManager";
import ConsultantList from "../components/manager/consultants/ConsultantList";
import ConsultantLedger from "../components/manager/consultants/ConsultantLedger";
import ConsultantServicesConsole from "../components/manager/consultants/ConsultantServicesConsole";
import LabManager from "../components/manager/labs/LabManager";
import CalendarView from "../components/manager/CalendarView";
import AccountantLedger from "../components/manager/AccountantLedger";
import ClinicNotesBoard from "../components/common/apexo/ClinicNotesBoard";
import ErrorBoundary from "../components/manager/shared/ErrorBoundary";
// Widgets
import LowStockWidget from "../components/manager/widgets/LowStockWidget";
import DayCashFlow from "../components/manager/DayCashFlow";

import PatientPaymentConsole from "../components/manager/PatientPaymentConsole";
import StatsDashboard from "../components/manager/StatsDashboard";
import PharmacySuppliersPanel from "../components/pharmacy/PharmacySuppliersPanel";
import LabPayables from "../components/manager/LabPayables";
import AppShellLayout from "../components/layout/AppShellLayout";
import ManagerModuleHeader, { ManagerModuleTab } from "../components/manager/ManagerModuleHeader";
import { useI18n } from "../i18n/I18nProvider";

import "./ManagerHome.css";
import "../components/manager/ModernManager.css";

interface LowStockItem {
  id?: string | number;
  is_low_stock?: boolean;
  [key: string]: unknown;
}

type ManagerTab = ManagerModuleTab;

export default function ManagerHome() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<ManagerTab>("dashboard");
  const [selectedConsultant, setSelectedConsultant] = useState<{ id: number; name: string; specialization?: string } | null>(null);
  const [lowStockItems, setLowStockItems] = useState<LowStockItem[]>([]);

  useEffect(() => {
    // Ortho removed from valid tabs
    const VALID_TABS: ManagerTab[] = ["dashboard", "billing", "inventory", "consultants", "labs", "reports", "patients", "notes", "stats", "ledgers"];
    const saved = localStorage.getItem("manager:activeTab");
    if (saved && VALID_TABS.includes(saved as ManagerTab)) {
      setActiveTab(saved as ManagerTab);
    }
    fetchLowStock();
  }, []);

  const switchTab = (tab: ManagerTab) => {
    setActiveTab(tab);
    localStorage.setItem("manager:activeTab", tab);
  };

  const fetchLowStock = async () => {
    try {
      const res = await apiGet("/api/manager/inventory");
      if (Array.isArray(res)) {
        setLowStockItems((res as LowStockItem[]).filter(i => i.is_low_stock));
      }
    } catch (e) {
      console.error("Failed to load stock alerts", e);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "dashboard":
        return (
          <div className="dashboard-content">
            <div className="dashboard-grid-main">
              {/* Left Column: Calendar */}
              <div className="dashboard-col-left">
                <div className="widget-wrapper mgr-widget">
                  <div className="card-header-row">
                    <h3>{t("manager_dashboard_clinical_operations")}</h3>
                    <div className="mgr-action-row">
                      <button
                        className="btn-primary"
                        onClick={() => navigate("/treatments")}
                      >
                        {t("manager_dashboard_launch_module")}
                      </button>
                      <button
                        className="btn-primary mgr-btn-sky"
                        onClick={() => navigate("/billing/clinical")}
                      >
                        {t("manager_dashboard_clinical_billing")}
                      </button>
                    </div>
                  </div>
                  <p className="muted mgr-muted-spacer">
                    {t("manager_dashboard_clinical_operations_sub")}
                  </p>
                </div>
                <CalendarView />
              </div>

              {/* Right Column: Key Metrics */}
              <div className="dashboard-col-right">
                <DayCashFlow />
                <LowStockWidget />
              </div>
            </div>
          </div>
        );
      case "billing":
        return <ManagerBilling />;
      case "inventory":
        return <InventoryManager />;
      case "consultants":
        return (
          <div className="consultants-layout">
            <div className="cons-list-panel">
              <ConsultantList
                selected={selectedConsultant}
                onSelect={setSelectedConsultant}
              />
            </div>
            <div className="cons-detail-panel">
              <ConsultantServicesConsole selectedConsultant={selectedConsultant} />
              {selectedConsultant ? (
                <ConsultantLedger consultant={selectedConsultant} />
              ) : (
                <div className="empty-selection">
                  {t("manager_consultant_select_hint")}
                </div>
              )}
            </div>
          </div>
        );
      case "labs":
        return <LabManager />;
      case "reports":
        return <AccountantLedger />;
      case "patients":
        return <PatientPaymentConsole />;
      case "notes":
        return <ClinicNotesBoard />;
      case "stats":
        return <StatsDashboard />;
      case "ledgers":
        return (
          <div className="mgr-ledger-stack">
            <div>
              <h3 className="mgr-ledger-title">💊 Drug Supplier Payables</h3>
              <PharmacySuppliersPanel />
            </div>
            <div>
              <h3 className="mgr-ledger-title">🔬 Lab Payables</h3>
              <LabPayables />
            </div>
          </div>
        );
      default:
        return <div>{t("common_module_not_found")}</div>;
    }
  };

  return (
    <AppShellLayout
      moduleId="manager"
      header={(
        <ManagerModuleHeader
          activeTab={activeTab}
          onSelectTab={switchTab}
          lowStockCount={lowStockItems.length}
        />
      )}
      left={undefined}
      leftCollapsible={false}
      main={(
        <ErrorBoundary>
          <div className="manager-workspace">
            {renderContent()}
          </div>
        </ErrorBoundary>
      )}
    />
  );
}
